import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";
import { 
  Search, 
  Trash2, 
  UserCheck,
  Calendar
} from "lucide-react";

export default function AdminTenants() {
  const { token } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [deletingTenant, setDeletingTenant] = useState(null);

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      const response = await axios.get(`${API}/admin/tenants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTenants(response.data);
    } catch (error) {
      toast.error("Failed to load tenants");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(
        `${API}/admin/tenants/${deletingTenant.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Tenant removed successfully");
      setDeletingTenant(null);
      fetchTenants();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove tenant");
    }
  };

  const filteredTenants = tenants.filter(tenant => 
    tenant.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tenant.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tenant.telephone?.includes(searchTerm)
  );

  return (
    <div className="flex min-h-screen bg-background" data-testid="admin-tenants-page">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 pt-20 md:pt-6 md:p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-primary">Tenants</h1>
              <p className="text-text-muted mt-1">View all approved tenants</p>
            </div>
            <div className="relative w-full md:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
              <Input
                data-testid="search-tenants-input"
                placeholder="Search by name, username, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-12 bg-white border-stone-200"
              />
            </div>
          </div>

          {/* Tenants Grid */}
          <Card className="border-stone-200">
            <CardHeader>
              <CardTitle className="text-lg">Approved Tenants ({filteredTenants.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-pulse text-primary">Loading...</div>
                </div>
              ) : filteredTenants.length === 0 ? (
                <div className="text-center py-8">
                  <UserCheck className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                  <p className="text-text-muted">No tenants found</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredTenants.map((tenant) => (
                    <div 
                      key={tenant.id} 
                      className="bg-stone-50 rounded-xl p-4 border border-stone-200"
                      data-testid={`tenant-card-${tenant.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                            <UserCheck className="w-6 h-6 text-green-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-primary">{tenant.full_name}</h3>
                            <p className="text-sm text-text-muted">@{tenant.username}</p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeletingTenant(tenant)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-tenant-btn-${tenant.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      <div className="mt-4 space-y-2 text-sm">
                        <div className="flex items-center gap-2 text-text-muted">
                          <span>Phone:</span>
                          <span className="text-text-main">{tenant.telephone}</span>
                        </div>
                        {tenant.email && (
                          <div className="flex items-center gap-2 text-text-muted">
                            <span>Email:</span>
                            <span className="text-text-main">{tenant.email}</span>
                          </div>
                        )}
                        {tenant.occupation && (
                          <div className="flex items-center gap-2 text-text-muted">
                            <span>Occupation:</span>
                            <span className="text-text-main">{tenant.occupation}</span>
                          </div>
                        )}
                        {tenant.approved_at && (
                          <div className="flex items-center gap-2 text-text-muted">
                            <Calendar className="w-4 h-4" />
                            <span>Approved: {new Date(tenant.approved_at).toLocaleDateString()}</span>
                          </div>
                        )}
                      </div>

                      <div className="mt-4">
                        <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                          Approved Tenant
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deletingTenant} onOpenChange={() => setDeletingTenant(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Tenant</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove {deletingTenant?.full_name} from the tenants list? This will revoke their tenant status.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-remove-tenant-btn"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
